import Icon from 'ui/Icon';
import { HStack, VStack } from 'ui/Layout';
import Popover from 'ui/Popover';
import { Text } from 'ui/Typography';

import { useSankeyExcelBindingContext } from '../../../../contexts/sankeyExcelBindingContext';
import { useSankeyFormContext } from '../../../../contexts/sankeyFormContext';
import { SankeyFlow } from '../../../../diagram/types';
import { useIsDataFromExcel } from '../../../DataEditor/utils';
import { useSankeyFlowInteraction } from '../../../SankeyInteractionProvider/utils';
import { centerOverTrigger } from '../../utils';
import FlowAmountInput from './FlowAmountInput';
import FlowNodeAutocomplete from './FlowNodeAutocomplete';
import FlowRemoveButton from './FlowRemoveButton';

interface Props {
  flow: SankeyFlow;
  children: React.ReactNode;
}

const FlowEditPopover: React.FC<Props> = ({ flow, children }) => {
  const { isFocused, handleFocus, handleBlur } = useSankeyFlowInteraction(flow);

  const { form } = useSankeyFormContext();
  const showComparisonLine = form.watch('show_comparison_line');
  const { hasExcelBindingInThisSheet } = useSankeyExcelBindingContext();
  const isDataFromExcel = useIsDataFromExcel();

  return (
    <Popover.Root
      lazyMount
      unmountOnExit
      open={isFocused}
      onOpenChange={(e) => (e.open ? handleFocus() : handleBlur())}
      positioning={centerOverTrigger}
    >
      <Popover.Trigger>{children}</Popover.Trigger>
      <Popover.Content zIndex="docked">
        <Popover.Arrow />
        <Popover.Body>
          <Popover.Title>
            {hasExcelBindingInThisSheet ? (
              <Text>Edit flow</Text>
            ) : isDataFromExcel ? (
              <Text>
                Edit flow{' '}
                <Icon color="yellow.500" icon="triangle-exclamation" />
              </Text>
            ) : (
              <HStack justifyContent="space-between">
                <Text>Edit flow</Text>
                <FlowRemoveButton flow={flow} />
              </HStack>
            )}
          </Popover.Title>
          {hasExcelBindingInThisSheet ? (
            <Text fontSize="xs">
              To edit the flow change the values of the Excel cells that this
              diagram was created from.
            </Text>
          ) : isDataFromExcel ? (
            <Text fontSize="xs">
              Flow data for this diagram is sourced from an Excel spreadsheet.
              Please update the cells in your spreadsheet to update the flow.
            </Text>
          ) : (
            <VStack alignItems="stretch">
              <FlowNodeAutocomplete label="From" field="source" flow={flow} />
              <FlowNodeAutocomplete label="To" field="target" flow={flow} />
              <HStack alignItems="flex-start">
                <FlowAmountInput
                  label="Amount, current"
                  field={`flows.${flow.id}.amount`}
                />
                {showComparisonLine && (
                  <FlowAmountInput
                    label="Amount, comparison"
                    field={`flows.${flow.id}.amountComparison`}
                    canBeEmpty
                  />
                )}
              </HStack>
            </VStack>
          )}
        </Popover.Body>
      </Popover.Content>
    </Popover.Root>
  );
};

export default FlowEditPopover;
